import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet';
import { Form, Button, Modal } from 'react-bootstrap';
import { FormFeedback } from 'reactstrap';
import { Formik, Field } from 'formik';
import { store } from '../../store';
import { companyNameReplace } from '../../utils';
import { Common } from '../../utils/constants';
import images from '../../assets/images';
import { ISignUpProps } from '../../types/propTypes';
import { SignUpValidationSchema } from '../../validation/login/login-validation';
import SocialMediaLoginForm from './SocialMediaLoginForm';
import { ConfirmationBox } from '../../components/common/ConfirmationBox';
import { SignUpFormHook } from './SignUpFormHook';
import DOMPurify from 'dompurify';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import RecaptchaV2Net from '../../components/RecaptchaV2Net';
import SelectBox from '../../components/reactstrap-formik/SelectBox';
import type { IRegionData } from '../../models/profile';
import ReactstrapFormikFloatingLabelInput from '../../components/reactstrap-formik/ReactstrapFormikFloatingLabelInput';
import ReactstrapFormikFloatingLabelPasswordInput from '../../components/reactstrap-formik/ReactstrapFormikFloatingLabelPasswordInput';
import TermsAndConditionModal from '../terms-conditions/TermsAndConditionModal';
import { env } from 'config/env';

const SignUpForm = ({ handleChangeFormValues, setEmail, setPassword, onChangeStage }: ISignUpProps) => {
  const { t } = useTranslation();
  const { executeRecaptcha } = useGoogleReCaptcha();
  const [isRecaptchaV2, setIsRecaptchaV2] = useState(false);
  const strengthRef = useRef('');

  const {
    formValues,
    modal,
    handleSubmit,
    closeModal,
    siteKey,
    recaptchaRef,
    colorStyles,
    selectedRegion,
    regions,
    handleRegionChange,
    termsAndConditionData,
    termsModal,
    handleTermsModal,
    closeTermsModal,
    acceptTermsFromModal,
    termsWarningModal,
    openTermsWarningModal,
    closeTermsWarningModal,
  } = SignUpFormHook({
    handleChangeFormValues,
    setEmail,
    setPassword,
    onChangeStage,
    onInvalidCaptcha: () => setIsRecaptchaV2(true),
  });

  const handleSubmitWithV3 = async (values, actions) => {
    if (values.term_and_condition.accepted) {
      actions.setFieldValue('recaptchaCode', '');
      actions.setFieldTouched('recaptchaCode', false);

      if (!executeRecaptcha) {
        console.error('Execute recaptcha not yet available');
        return;
      }

      if (!isRecaptchaV2 && executeRecaptcha) {
        try {
          const token = await executeRecaptcha('signup');
          if (token) {
            values.recaptchaCode = token;
            return handleSubmit(values, true);
          }
          setIsRecaptchaV2(true);
          return;
        } catch {
          setIsRecaptchaV2(true);
          return;
        }
      }
      return handleSubmit(values, false);
    } else {
      openTermsWarningModal();
    }
  };

  return (
    <div className="signup-form-container font-poppins">
      <Helmet>
        <title>{companyNameReplace(t('signup.title'))} </title>
      </Helmet>
      <button
        type="button"
        className="btn btn-icon btn-link pr-0 btn-back"
        onClick={() => onChangeStage(Common.AuthStage.SIGNIN)}
        title={t<string>('signup.button.back')}
      >
        <img className="icon" src={images.backArrowRSVG} alt={t<string>('forgot.button.back')} />
      </button>
      <div className="sign-up-header">
        <div className="sign-up-icon-container">
          <img className="sign-up-icon" src={images.loginIconSVG} alt={t<string>('navbar.title')} />
        </div>
        <h1 className="title sign-up-title mt-3">{t('signup.header')}</h1>
        <p className="sign-up-sub-header">{t('signup.subHeader')}</p>
      </div>

      <Formik
        initialValues={formValues}
        onSubmit={handleSubmitWithV3}
        validationSchema={SignUpValidationSchema(t, isRecaptchaV2, strengthRef)}
        enableReinitialize
      >
        {(props) => {
          const { handleSubmit, values, setFieldValue } = props;
          return (
            <>
              <form className="form-secondary" onSubmit={handleSubmit} autoComplete="off" noValidate>
                <div className="sign-up-select-region">
                  <SelectBox
                    styles={colorStyles}
                    name="region"
                    menuPlacement="bottom"
                    menuPosition="fixed"
                    value={selectedRegion}
                    options={regions}
                    onChange={(e: IRegionData) => {
                      handleRegionChange(e, values.email);
                    }}
                    getOptionLabel={(opt: IRegionData) => opt.name as string}
                    getOptionValue={(opt: IRegionData) => opt.id as number}
                    isClearable={false}
                    placeholder={t('login.placeholder.region')}
                    formatOptionLabel={(option: IRegionData) => (
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                        }}
                      >
                        <img
                          src={`${env.VITE_DRIVER_PORTAL_URL}${option.image_url}`}
                          alt={option.name}
                          style={{
                            width: '20px',
                            height: '15px',
                            objectFit: 'cover',
                          }}
                        />
                        <span>{option.name}</span>
                      </div>
                    )}
                    arrowSize="large"
                    menuPortalTarget={document.body}
                  />
                </div>
                <div className="mt-4 mb-4">
                  <Field
                    type="text"
                    name="email"
                    id="Email"
                    label={t('signup.form.email')}
                    component={ReactstrapFormikFloatingLabelInput}
                    maxLength={50}
                    rightIcon={images.mailSVG}
                  />
                </div>

                <Field
                  type="password"
                  name="password"
                  id="Password"
                  label={t('signup.form.password')}
                  autoComplete="off"
                  component={ReactstrapFormikFloatingLabelPasswordInput}
                  isShowStrengthBar={true}
                  passwordStrengthRef={strengthRef}
                />
                <div className="custom-control custom-checkbox custom-control-inline signup-terms-and-condition mb-3">
                  <input
                    type="checkbox"
                    className="custom-control-input"
                    id="term_and_condition"
                    name="term_and_condition"
                    checked={values.term_and_condition.accepted}
                    onChange={() => setFieldValue('term_and_condition.accepted', !values.term_and_condition.accepted)}
                  />
                  <label className="custom-control-label sign-up-checkbox-label pb-0" htmlFor="term_and_condition">
                    {t('termsAndConditionListing.label.iAggreeToTermsCondition')}
                    {termsAndConditionData && (
                      <span
                        onClick={(e) => {
                          e.preventDefault();
                          handleTermsModal(termsAndConditionData.content, true, () =>
                            acceptTermsFromModal(setFieldValue),
                          );
                        }}
                        className="term-and-condition"
                        style={{
                          cursor: 'pointer',
                          color: 'var(--themeColor)',
                          marginLeft: '4px',
                        }}
                      >
                        {t('signup.form.condition')}
                      </span>
                    )}
                  </label>
                </div>
                <input type="hidden" value={values.recaptchaCode} name="recaptchaCode" />
                {isRecaptchaV2 && siteKey && (
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'center',
                      flexDirection: 'column',
                      alignItems: 'center',
                    }}
                    className="mb-3"
                  >
                    {isRecaptchaV2 && siteKey && (
                      <RecaptchaV2Net
                        ref={recaptchaRef}
                        sitekey={siteKey}
                        onChange={(value) => {
                          props.setFieldValue('recaptchaCode', value);
                          props.setFieldTouched('recaptchaCode', true);
                        }}
                        hl={
                          store?.getState()?.UserData?.language?.code ||
                          store?.getState()?.LanguageInfo?.defaultLanguage?.code
                        }
                        key={
                          store?.getState()?.UserData?.language?.code ||
                          store?.getState()?.LanguageInfo?.defaultLanguage?.code
                        }
                      />
                    )}
                    <div className="text-left codeFixWidth">
                      {props.touched.recaptchaCode &&
                        props.errors.recaptchaCode !== '' &&
                        props.values.recaptchaCode == '' && (
                          <FormFeedback className="pb-0 error invalid-feedback d-block" type="invalid">
                            {props.errors.recaptchaCode}
                          </FormFeedback>
                        )}
                    </div>
                  </div>
                )}

                <Form.Group className=" form-group">
                  <Button type="submit" className="btn btn-primary w-100 sign-up-button">
                    {t('signup.button.submit')}
                  </Button>
                </Form.Group>
                <div className="clearfix">
                  <p className="sign-up sign-up-text">
                    {t('signup.label.accountNew')}{' '}
                    <a
                      href="#"
                      title={t<string>('signup.button.signin')}
                      onClick={(e) => {
                        e.preventDefault();
                        onChangeStage(Common.AuthStage.SIGNIN);
                      }}
                      className="login-btn"
                    >
                      {t('signup.button.signin')}
                    </a>
                  </p>
                </div>
                <SocialMediaLoginForm termsAndCondition={values.term_and_condition} action={'signup'} />
              </form>

              <TermsAndConditionModal
                modal={termsModal}
                isShowCloseButton={true}
                closeTermsModal={closeTermsModal}
                isShowAccceptButton={true}
              />

              <Modal show={termsWarningModal.showModal} centered className="terms-warning-modal rounded font-poppins">
                <Modal.Body className="p-0">
                  <div className="d-flex justify-content-end">
                    <button type="button" className="close m-1" onClick={() => closeTermsWarningModal(null)}>
                      <span aria-hidden="true">×</span>
                      <span className="sr-only">{t('Payment.Model.button.close')}</span>
                    </button>
                  </div>

                  <div className="terms-and-conditions-title m-3" style={{ fontSize: '19px' }}>
                    {t('signup.termsAndConditionModal.header')}
                  </div>
                  <p className="p-0 pt-2 pl-4 pr-2 mb-3 terms-and-condition-message">
                    {t('signup.termsAndConditionModal.message')}
                  </p>
                </Modal.Body>
                <Modal.Footer>
                  <Button
                    className="btn btn-primary w-100 mx-5 terms-and-condition-accept-button"
                    onClick={() => closeTermsWarningModal(setFieldValue)}
                    title={t<string>('signup.termsAndConditionModal.button.accept')}
                  >
                    {t('signup.termsAndConditionModal.button.accept')}
                  </Button>
                </Modal.Footer>
              </Modal>
            </>
          );
        }}
      </Formik>

      <ConfirmationBox
        onCancel={closeModal}
        onConfirm={modal.callBack}
        header={modal.header}
        bodyMessage={
          <div
            dangerouslySetInnerHTML={{
              __html: DOMPurify.sanitize(modal.body, {
                ALLOWED_TAGS: ['span'],
                ALLOWED_ATTR: ['class'],
              }),
            }}
          ></div>
        }
        body={
          <div
            dangerouslySetInnerHTML={{
              __html: DOMPurify.sanitize(modal.body, {
                ALLOWED_TAGS: ['span'],
                ALLOWED_ATTR: ['class'],
              }),
            }}
          ></div>
        }
        showModel={modal.showModel}
        closeModel={closeModal}
      />
    </div>
  );
};
export default SignUpForm;
